import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { ArrowLeft, Pencil, Trash } from "lucide-react";

import Header from "../Header";
import LeftNavBar from "../LeftNavBar";
import PageHeader from "../PageHeader";
import { PageHeaderName } from "../../StyledCss/styledComponents";
import { PrimaryButton, SecondaryButton } from "../../StyledCss/styledbtn";
import LoadingView from "../LoadingView";
import FailureView from "../FailureView";

import toast from "react-hot-toast";

import {
  getUserData,
  addWorkLog,
  getWorkLogs,
  deleteWorkLog,
  updateWorklog,
  getProjectsByEmpId,
  exportWorklogs,
  exportWorklogsZip
} from "../../api/services/hroneApi";
import { convertKeysToCamelCase } from "../../util/convertCamelCase";
import { convertKeysToSnakeCase } from "../../util/convertToSnakeCase";
import PopupModal from "../PopupModal";
import AddWorkLogPopup from "../AddWorkLogPopUp";
import SlidingModal from "../SlidingModal/slidingModal";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { usePermissions } from "../../redux/slices/permissionsSlice";
import { FEATURE_CODES } from "../../util/featureCodes";

const apiStatusConstants = {
  initial: "INITIAL",
  success: "SUCCESS",
  failure: "FAILURE",
  inProgress: "IN_PROGRESS",
};

const WorkLogs = () => {
  const { hasPermission } = usePermissions();
  const canExport = hasPermission(FEATURE_CODES.WORKLOG_EXPORT);
  const canEdit = hasPermission(FEATURE_CODES.WORKLOG_EDIT);
  const canDelete = hasPermission(FEATURE_CODES.WORKLOG_DELETE);
  const canViewAll = hasPermission(FEATURE_CODES.WORKLOG_VIEW_ALL);
  const { project_id,employee_id, year, month } = useParams();
  const navigate = useNavigate();
  const { empId } = getUserData();
  const location=useLocation()
  const isAdminViewingEmployeeWorklogs = location.state?.isAdminViewingEmployeeWorklogs || false;
  const [apiStatus, setApiStatus] = useState(apiStatusConstants.initial);
  const [projectsData, setProjectsData] = useState([]);
  const [workLogsData, setWorkLogsData] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [editingWorkLog, setEditingWorkLog] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [workLogToDelete, setWorkLogToDelete] = useState(null);
  const [loadingStates, setLoadingStates] = useState({
    addWorklog: false,
    updateWorklog: false,
    deleteWorklog: false,
    exportMonth: false,
    exportCustom: false,
  });
    const [errorMessages, setErrorMessages] = useState({
    addWorklog: "",
    updateWorklog: "", 
  });

  const setLoading = (operation, isLoading) => {
    setLoadingStates((prev) => ({ ...prev, [operation]: isLoading }));
  };
  const fetchWorkLogs = useCallback(
    async (projectId) => {
      try {
        setApiStatus(apiStatusConstants.inProgress);
        const projectToFetch = isAdminViewingEmployeeWorklogs ? project_id : projectId;
        const employeeIdToFetch = isAdminViewingEmployeeWorklogs ? employee_id : empId;
        const workLogsResponse = await getWorkLogs(year, month, employeeIdToFetch, projectToFetch);

        if (workLogsResponse.ok) {
          const workLogsData = await workLogsResponse.json();
          setWorkLogsData(workLogsData.map(convertKeysToCamelCase));
          setApiStatus(apiStatusConstants.success);
        } else {
          setApiStatus(apiStatusConstants.failure);
        }
      } catch (error) {
        setApiStatus(apiStatusConstants.failure);
        toast.error("Failed to load work logs");
      }
    },
    [year, month, employee_id, project_id, isAdminViewingEmployeeWorklogs,empId],
  );

  const fetchProjects = useCallback(async () => {
    try {
      const projectsResponse = await getProjectsByEmpId();

      if (projectsResponse.ok) {
        const projectsData = await projectsResponse.json();
        setProjectsData(projectsData.map(convertKeysToCamelCase));
      } 
    } catch (error) {
      toast.error("Failed to load projects");
    }
  }, []);
useEffect(() => {
    if (!isAdminViewingEmployeeWorklogs) {
      fetchProjects();
    }
  }, [isAdminViewingEmployeeWorklogs, fetchProjects]);

  useEffect(() => {
    if (project_id) {
      setSelectedProject(project_id);
    }
  }, [project_id]);

  useEffect(() => {
    fetchWorkLogs(selectedProject);
  }, [selectedProject, fetchWorkLogs]);

  useEffect(() => {
    if (!canViewAll) {
      navigate(-1);
    }
  }, [canViewAll, navigate]);


  const handleThisMonth = async () => {
    try {
      const projectToExport = isAdminViewingEmployeeWorklogs ? project_id : selectedProject;
      const employeeIdToExport = isAdminViewingEmployeeWorklogs ? employee_id : empId;
      setLoading("exportMonth", true);
      const startDate = `${year}-${String(month).padStart(2, "0")}-01`;
      const lastDay = new Date(Number(year), Number(month), 0);
      const endDate = `${lastDay.getFullYear()}-${String(lastDay.getMonth() + 1).padStart(2, "0")}-${String(lastDay.getDate()).padStart(2, "0")}`;
       let response;
            if (!projectToExport) {
              const projectIds = projectsData
                .flatMap(project => project.projectId || [])
                .join(',');
              response = await exportWorklogsZip(projectIds, startDate, endDate);
            } else {
              response = await exportWorklogs(projectToExport,employeeIdToExport, startDate, endDate);
            } 

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const contentDisposition = response.headers.get("content-disposition");
        let filename;
         if (contentDisposition) {
          const match = contentDisposition.match(/filename="?(.+?)"?$/);
          if (match) {
            filename = match[1];
          }
        }
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("Worklogs exported successfully");
      } else {
        toast.error("Failed to export worklogs");
      }
    } catch (error) {
      toast.error("Failed to export worklogs. Please try again.");
    } finally {
      setLoading("exportMonth", false);
    }
  }
  const handleDeleteWorkLog = useCallback(
    async (worklog) => {
      try {
        setLoading("deleteWorklog", true);
        const response = await deleteWorkLog(worklog.id);

        if (response.status === 200) {
          toast.success("Work log deleted successfully");
          await fetchWorkLogs(selectedProject);
        } else {
          toast.error("Failed to delete work log");
        }
      } catch (error) {
        toast.error("Failed to delete work log. Please try again.");
      } finally {
        setLoading("deleteWorklog", false);
      }
    },
    [selectedProject, fetchWorkLogs],
  );

  const handleDeleteClick = useCallback((worklog) => {
    setWorkLogToDelete(worklog);
    setIsDeleteModalOpen(true);
  }, []);

  const cancelDelete = useCallback(() => {
    setIsDeleteModalOpen(false);
    setWorkLogToDelete(null);
  }, []);

  const confirmDelete = useCallback(() => {
    if (workLogToDelete) {
      handleDeleteWorkLog(workLogToDelete);
    }
    setIsDeleteModalOpen(false);
    setWorkLogToDelete(null);
  }, [workLogToDelete, handleDeleteWorkLog]);

  const handleEditWorkLog = useCallback((worklog) => {
    setEditingWorkLog(worklog);
    setIsPopupOpen(true);
  }, []);

  const resetForm = useCallback(() => {
    setEditingWorkLog(null);
    setIsPopupOpen(false);
    setErrorMessages({ addWorklog: "", updateWorklog: "" });
  }, []);

  const handleCardClick = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleProjectChange = useCallback((e) => {
    setSelectedProject(e.target.value);
  }, []);

  const handleWorkLogSubmit = useCallback(
    async (data) => {
      const workLogData = convertKeysToSnakeCase({
        ...data,
        employeeId: empId,
      });

      try {
        setLoading(editingWorkLog ? "updateWorklog" : "addWorklog", true);

        const response = editingWorkLog
          ? await updateWorklog({ id: editingWorkLog.id, ...workLogData })
          : await addWorkLog(workLogData);

        if (response.ok) {
          toast.success(
            editingWorkLog
              ? "Work log updated successfully"
              : "Work log added successfully",
          );
          await fetchWorkLogs(selectedProject);
          resetForm();
        } else {
          const errorData = await response.json();
          setErrorMessages(prev => ({...prev, [editingWorkLog ? "updateWorklog" : "addWorklog"]: errorData.detail }));
        }
      } catch (error) {
        console.error(`Error:`, error);
        toast.error("An error occurred. Please try again.");
      } finally {
        setLoading(editingWorkLog ? "updateWorklog" : "addWorklog", false);
      }
    },
    [empId, editingWorkLog, selectedProject, fetchWorkLogs, resetForm],
  );

  const onClickRetry = useCallback(
    () => fetchWorkLogs(selectedProject),
    [selectedProject, fetchWorkLogs],
  );
  const columnDefs = useMemo(
    () => {
      const baseColumns = [
        { headerName: "DATE", field: "workDate", width: 150 ,suppressMenu: true},
        { headerName: "HOURS", field: "hours", width: 150, suppressMenu: true },
        { headerName: "PROJECT NAME", field: "projectName", width: 240, suppressMenu: true },
        {
          headerName: "MODULE NAME",
          field: "moduleName",
          width: 200,
          suppressMenu: true,
          cellRenderer: (params) => params.value || "-",
          cellStyle: (params) => ({
            textAlign: params.value ? "left" : "center"
          }),
        },
        {
          headerName: "WORK DESCRIPTION",
          field: "dayPlan",
          width: 400,
          tooltipField: "dayPlan",
          suppressMenu: true,
          cellRenderer: (params) => params.value || "-",
          cellStyle: (params) => ({
            textAlign: params.value ? "left" : "center"
          }),
        },
        {
          headerName: "ATTENDANCE TYPE",
          field: "attendanceType",
          suppressMenu: true,
          width: 200,
          cellRenderer: (params) => 
            params.value ? params.value.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : '-'
        },
      ];

      if (!isAdminViewingEmployeeWorklogs && (canEdit || canDelete)) {
        baseColumns.push({
          headerName: "ACTIONS",
          width: 100,
          cellRenderer: (params) => params.data.active === false ? null : (
            <div
              style={{
                display: "flex",
                gap: "10px",
                alignItems: "center",
                height: "100%",
              }}
            >
              {canEdit && (
                <div title="Edit" className="icon-wrapper">
                  <Pencil
                    style={{
                      cursor: "pointer",
                      color: "#28a745",
                      fontSize: "20px",
                    }}
                    onClick={() => handleEditWorkLog(params.data)}
                  />
                </div>
              )}
              {canDelete && (
                <div title="Delete" className="icon-wrapper">
                  <Trash
                    style={{
                      cursor: "pointer",
                      color: "#dc3545",
                      fontSize: "16px",
                    }}
                    onClick={() => handleDeleteClick(params.data)}
                  />
                </div>
              )}
            </div>
          ),
          sortable: false,
          filter: false,
          floatingFilter: false,
        });
      }

      return baseColumns;
    },
    [handleEditWorkLog, handleDeleteClick, isAdminViewingEmployeeWorklogs, canEdit, canDelete],
  );

  const defaultColDef = useMemo(
    () => ({
      cellStyle: { fontSize: "14px" },
      sortable: true,
      filter: true,
      floatingFilter: true,
    }),
    [],
  );


  const renderTable = useCallback(
    () => (
      <div
        className="ag-theme-alpine"
        style={{
          height: "78vh",
          fontSize: "14px",
          marginLeft: "20px",
          marginRight: "20px",
          marginTop: "20px",
          fontFamily: "Arial, Helvetica, sans-serif",
          backgroundColor: "#FFFFFF",
        }}
      >
        <AgGridReact
          columnDefs={columnDefs}
          rowData={workLogsData}
          pagination={true}
          paginationPageSize={10}
          defaultColDef={defaultColDef}
          rowSelection="multiple"
          suppressRowClickSelection={true}
          enableCellTextSelection={true}
        />
      </div>
    ),
    [columnDefs, workLogsData, defaultColDef],
  );

  const renderData = () => {
    switch (apiStatus) {
      case apiStatusConstants.inProgress:
        return <LoadingView />;
      case apiStatusConstants.success:
        return renderTable();
      case apiStatusConstants.failure:
        return <FailureView onClickRetry={onClickRetry} />;
      default:
        return null;
    }
  };

  if (!canViewAll) {
    return null;
  }

  return (
    <div>

      <Header />
      <div className="d-flex">
        <LeftNavBar />
        <div className="page-content-wrapper">
          <PageHeader>
            <PageHeaderName>
              <ArrowLeft size={20} 
              onClick={handleCardClick}
              style={{ cursor: "pointer" }} /> Work Logs
            </PageHeaderName>
            <div style={{ display: "flex", gap: "15px", alignItems: "center" }}>
              {!isAdminViewingEmployeeWorklogs && <select
                className="selectElement"
                value={selectedProject}
                onChange={handleProjectChange}
              >
                <option value="">All Projects</option>
                 {projectsData.length === 0 ? (
                      <option value="" disabled>
                        No projects available
                      </option>
                    ) : (
                      projectsData.map((project) => (
                        <option
                          key={project.projectId}
                          value={project.projectId}
                        >
                          {project.projectName}
                        </option>
                      ))
                    )}
              </select>}

              {canExport && (
              <div
                className="dropdown"
                style={{ position: "relative" }}
                title={!project_id && projectsData.length === 0 ? "No projects assigned yet" : ""}
              >
                <PrimaryButton
                  style={{width : "160px"}}
                  onClick={handleThisMonth}
                  disabled={(!project_id && projectsData.length === 0) || workLogsData.length === 0}
                >{loadingStates.exportMonth
                        ? "Downloading..."
                        : "Download Timesheet"} </PrimaryButton>

              </div>
              )}
            </div>
          </PageHeader>

          <div className="table-container">{renderData()}</div>
        </div>
      </div>

      <SlidingModal
        modalName={editingWorkLog ? "Update WorkLog" : "Add WorkLog"}
        closeBtn={resetForm}
        isModalOpen={isPopupOpen}
        marginBottom="0px"
      >
        <AddWorkLogPopup
          onSubmitData={handleWorkLogSubmit}
          closeBtn={resetForm}
          defaultValues={editingWorkLog}
          isEditMode={!!editingWorkLog}
          selectedProjectId={selectedProject}
          isLoading={loadingStates.addWorklog || loadingStates.updateWorklog}
          errorMsg={ errorMessages.updateWorklog || errorMessages.addWorklog}
          onClearError={() => setErrorMessages({  updateWorklog: "" })}
          projectsData={projectsData}
        />
      </SlidingModal>

      <PopupModal
        showModal={isDeleteModalOpen}
        modalName="Confirm Delete"
        closeBtn={cancelDelete}
        marginBottom="0px"
      >
        <div>
          <p>Are you sure you want to delete this work log?</p>
          <div
            style={{
              display: "flex",
              gap: "10px",
              justifyContent: "center",
              marginTop: "15px",
            }}
          >
            <SecondaryButton onClick={cancelDelete}>No</SecondaryButton>
            <PrimaryButton
              onClick={confirmDelete}
              disabled={loadingStates.deleteWorklog}
            >
              {loadingStates.deleteWorklog ? "Deleting..." : "Yes"}
            </PrimaryButton>
          </div>
        </div>
      </PopupModal>
    </div>
  );
};

export default WorkLogs;

// The Add Worklogs code was kept unchanged even after removing the Add Worklog button, as it may be useful in the future.